import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { MobileSheet } from "@/components/ui/mobile-sheet";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { useCreateListingDialog } from "@/contexts/create-listing-dialog-provider";
import { useListings } from "@/contexts/listings-provider";
import { useTeams } from "@/contexts/teams-provider";
import { useUser } from "@/contexts/user-provider";
import { useUsers } from "@/contexts/users-provider";
import { useApi } from "@/hooks/useApi";
import { useDeviceFormat } from "@/hooks/useDeviceFormat";
import { useTranslation } from "@/hooks/useTranslation";
import { createListingBatchAttacher } from "@/lib/listing-batch-attacher";
import {
  formatSkippedDescription,
  type ResolvedCompanies,
} from "@/lib/resolved-companies";
import { PlusCircledIcon } from "@radix-ui/react-icons";
import { ListingDTO } from "@shaarp/common";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FolderInputIcon,
  Loader2Icon,
} from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { toast, useToastError } from "@/components/ui/use-toast";

type CompaniesListingActionProps = {
  /** Ids known upfront: a new list is then created already filled, in one request. */
  readonly companiesIds?: readonly string[];
  /** Ids only knowable once the user commits — a chat table resolves its
   *  mentions, which may create and enrich companies, so it is only called
   *  once a list is picked or created, never while the picker or the creation
   *  dialog is still open. `onResolved` hears each id as it is found, those
   *  found before the call included. */
  readonly resolveCompaniesIds?: (
    onResolved?: (companyId: string) => void
  ) => Promise<ResolvedCompanies>;
  /** Context handed to the creation dialog so it can pre-fill title and description.
   *  A function is read when the dialog opens, for context that is not known upfront. */
  readonly searchQuery?: string | (() => string | undefined);
  readonly disabled?: boolean;
  readonly triggerClassName?: string;
  /** Companies are attached server-side; the caller refreshes its own view. */
  readonly onAttached?: (listing: ListingDTO) => void;
};

/**
 * "Save to a list" picker for a set of companies: pick an existing list — one of
 * the user's, of Shaarp's, or of a team member's — or create a new one.
 */
export function CompaniesListingAction({
  companiesIds,
  resolveCompaniesIds,
  searchQuery,
  disabled,
  triggerClassName,
  onAttached,
}: CompaniesListingActionProps) {
  const toastError = useToastError();
  const api = useApi();
  const { t } = useTranslation();
  const { isMobile } = useDeviceFormat();
  const {
    userListings,
    shaarpListings,
    collaboratorsListings,
    apiFetchListings,
    apiFetchCollaboratorsListings,
    apiFetchLoading,
    trackListingPopulation,
  } = useListings();
  const { isAdmin, canViewCollaborators } = useTeams();
  const { teamUser } = useUser();
  const { users, apiFetchUsers } = useUsers();
  const { openCreateListingDialog } = useCreateListingDialog();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isPending, setIsPending] = useState<boolean>(false);
  const [viewUserId, setViewUserId] = useState<string | null>(null);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open);
      if (!open) {
        setViewUserId(null);
        return;
      }
      apiFetchListings();
      if (canViewCollaborators) {
        apiFetchCollaboratorsListings();
        apiFetchUsers();
      }
    },
    [
      apiFetchCollaboratorsListings,
      apiFetchListings,
      apiFetchUsers,
      canViewCollaborators,
    ]
  );

  const companyUserListings = userListings.filter(
    (l) => l.listingType === "companies"
  );
  const companyShaarpListings = isAdmin
    ? shaarpListings.filter((l) => l.listingType === "companies")
    : [];

  const teamMembers = useMemo(
    () =>
      canViewCollaborators ? users.filter((u) => u.id !== teamUser?.id) : [],
    [users, teamUser?.id, canViewCollaborators]
  );

  const viewedUser = teamMembers.find((u) => u.id === viewUserId);
  const viewedUserListings = viewUserId
    ? (
        collaboratorsListings.find((c) => c.user.id === viewUserId)?.listings ??
        []
      ).filter((l) => l.listingType === "companies" && l.ownerId === viewUserId)
    : [];

  const hasRootContent =
    companyUserListings.length > 0 ||
    companyShaarpListings.length > 0 ||
    teamMembers.length > 0;

  /**
   * One toast for the whole save — what joined the list, what was left out
   * and why, what the list refused — rather than a success toast the failure
   * one replaces before it is read (one toast shows at a time).
   */
  const notifyAttached = useCallback(
    async (
      listing: ListingDTO,
      addedCount: number,
      skipped: ResolvedCompanies["skipped"],
      notAttachedCount: number = 0
    ) => {
      onAttached?.(listing);
      await apiFetchListings({ force: true });
      const description = [
        t("components.companiesListingAction.companiesAdded", {
          count: addedCount,
        }),
        formatSkippedDescription(skipped, t),
        notAttachedCount > 0
          ? t("components.companiesListingAction.notAttached", {
              count: notAttachedCount,
            })
          : "",
      ]
        .filter(Boolean)
        .join(" ");
      toast({
        variant: "default",
        title: t("components.companiesListingAction.addToList", {
          title: listing.title,
        }),
        description,
      });
    },
    [apiFetchListings, onAttached, t]
  );

  const attachCompanies = useCallback(
    async (listing: ListingDTO) => {
      setIsPending(true);
      try {
        // Resolving the companies takes long enough for the list — already saved
        // when it was just created — to be browsable while still empty.
        await trackListingPopulation(listing.id, async () => {
          // Each company joins the list as soon as it is found: a page left
          // mid-way (closed, reloaded, discarded by the browser) leaves the
          // list holding what was found, not empty.
          const attacher = createListingBatchAttacher(async (ids) => {
            await api.post(`/listing/${listing.id}/companies`, {
              companiesIds: ids,
            });
          });
          const resolved: ResolvedCompanies = resolveCompaniesIds
            ? await resolveCompaniesIds(attacher.add)
            : { companiesIds: companiesIds ?? [], skipped: [] };

          if (resolved.companiesIds.length === 0) {
            toastError({
              title: t("components.companiesListingAction.noneResolvedTitle"),
              description:
                formatSkippedDescription(resolved.skipped, t) ||
                t("components.companiesListingAction.noneResolvedDescription"),
            });
            return;
          }

          resolved.companiesIds.forEach(attacher.add);
          const { attachedIds, failedIds } = await attacher.finish();
          // The batches sent before a failure are in the list: its counter
          // and the toast say so, and the failure is reported beside them.
          if (attachedIds.length > 0) {
            await notifyAttached(
              listing,
              attachedIds.length,
              resolved.skipped,
              failedIds.length
            );
          } else {
            toastError();
          }
        });
      } catch {
        toastError();
      } finally {
        setIsPending(false);
      }
    },
    [
      api,
      companiesIds,
      notifyAttached,
      resolveCompaniesIds,
      t,
      toastError,
      trackListingPopulation,
    ]
  );

  const selectListing = useCallback(
    (listing: ListingDTO) => {
      handleOpenChange(false);
      attachCompanies(listing);
    },
    [attachCompanies, handleOpenChange]
  );

  const createListing = useCallback(
    (assigneeId?: string) => {
      handleOpenChange(false);
      // Companies known upfront travel with the creation request; the others are
      // resolved once the list exists — resolving creates and enriches them, and
      // the dialog can still be cancelled.
      const preFilledIds = resolveCompaniesIds ? undefined : companiesIds;
      openCreateListingDialog({
        defaultListingType: "companies",
        defaultAssigneeId: assigneeId,
        searchQuery:
          typeof searchQuery === "function" ? searchQuery() : searchQuery,
        companiesIds: preFilledIds,
        onCreated: preFilledIds
          ? (listing) => notifyAttached(listing, preFilledIds.length, [])
          : attachCompanies,
      });
    },
    [
      attachCompanies,
      companiesIds,
      handleOpenChange,
      notifyAttached,
      openCreateListingDialog,
      resolveCompaniesIds,
      searchQuery,
    ]
  );

  const saveToListLabel = t("components.companiesListingAction.saveToList");

  const listingItem = (listing: ListingDTO) => (
    <CommandItem
      key={listing.id}
      value={listing.title}
      onSelect={() => selectListing(listing)}
      className="text-sm"
    >
      {listing.title}
      {listing.companiesCount === 0 ? (
        <Badge variant="outline" className="ml-auto">
          {t("components.companiesListingAction.empty")}
        </Badge>
      ) : (
        <Badge variant="default" className="ml-auto">
          {listing.companiesCount}
        </Badge>
      )}
    </CommandItem>
  );

  const pickerContent = (
    <Command>
      {viewUserId && viewedUser ? (
        <CommandList>
          <CommandGroup>
            <CommandItem
              onSelect={() => setViewUserId(null)}
              className="text-sm text-muted-foreground"
            >
              <ChevronLeftIcon className="mr-2 w-4 h-4" />
              {viewedUser.fullName}
            </CommandItem>
          </CommandGroup>
          <CommandSeparator />
          <CommandGroup>
            <CommandItem onSelect={() => createListing(viewedUser.id)}>
              <PlusCircledIcon className="mr-2 w-5 h-5" />
              {t("components.companiesListingAction.newListFor", {
                name: viewedUser.firstName || viewedUser.fullName,
              })}
            </CommandItem>
          </CommandGroup>
          <CommandInput
            placeholder={t("components.companiesListingAction.search")}
          />
          <CommandEmpty>
            {t("components.companiesListingAction.noListFound")}
          </CommandEmpty>
          {viewedUserListings.length > 0 ? (
            <CommandGroup>{viewedUserListings.map(listingItem)}</CommandGroup>
          ) : (
            <CommandGroup>
              <CommandItem disabled={true}>
                {t("components.companiesListingAction.noListForMember")}
              </CommandItem>
            </CommandGroup>
          )}
        </CommandList>
      ) : (
        <>
          {hasRootContent ? (
            <CommandList className={isMobile ? "max-h-none flex-1" : undefined}>
              <CommandInput
                placeholder={t("components.companiesListingAction.search")}
                classNameWrapper={isMobile ? "px-4" : undefined}
                className={isMobile ? "h-12 text-base" : undefined}
              />
              <CommandEmpty>
                {t("components.companiesListingAction.noListFound")}
              </CommandEmpty>
              <CommandGroup
                key="listings"
                heading={t("components.companiesListingAction.myLists")}
              >
                {companyUserListings.length === 0 &&
                companyShaarpListings.length === 0 ? (
                  <CommandItem disabled={true}>
                    {t("components.companiesListingAction.noListCreated")}
                  </CommandItem>
                ) : (
                  [...companyUserListings, ...companyShaarpListings].map(
                    listingItem
                  )
                )}
              </CommandGroup>
              {teamMembers.length > 0 && (
                <CommandGroup
                  key="team"
                  heading={t("components.companiesListingAction.team")}
                >
                  {teamMembers.map((member) => (
                    <CommandItem
                      key={member.id}
                      value={member.fullName}
                      onSelect={() => setViewUserId(member.id)}
                      className="flex gap-2 items-center text-sm"
                    >
                      <Avatar className="w-5 h-5">
                        <AvatarImage src={member.imageUrl} />
                        <AvatarFallback className="text-xs">
                          {member.fullName?.charAt(0) || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate">{member.fullName}</span>
                      <ChevronRightIcon className="ml-auto w-4 h-4 opacity-50" />
                    </CommandItem>
                  ))}
                </CommandGroup>
              )}
            </CommandList>
          ) : (
            <CommandGroup
              heading={t("components.companiesListingAction.myLists")}
            >
              <CommandItem disabled={true}>
                {apiFetchLoading ? (
                  <Loader2Icon className="w-4 h-4 animate-spin" />
                ) : (
                  t("components.companiesListingAction.noListCreated")
                )}
              </CommandItem>
            </CommandGroup>
          )}
          <CommandSeparator />
          <CommandList>
            <CommandGroup>
              <CommandItem
                onSelect={() => createListing()}
                className={isMobile ? "py-3 px-3 text-[15px]" : undefined}
              >
                <PlusCircledIcon className="mr-2 w-5 h-5" />
                {t("components.companiesListingAction.newList")}
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </>
      )}
    </Command>
  );

  const trigger = (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={triggerClassName}
      disabled={disabled || isPending}
      aria-expanded={isOpen}
      onClick={isMobile ? () => handleOpenChange(true) : undefined}
    >
      {isPending ? (
        <Loader2Icon className="mr-2 w-4 h-4 animate-spin" />
      ) : (
        <FolderInputIcon className="mr-2 w-4 h-4" />
      )}
      {saveToListLabel}
    </Button>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <MobileSheet
          open={isOpen}
          onOpenChange={handleOpenChange}
          heightClass="h-[80dvh]"
        >
          <div className="px-5 pt-2 pb-3 shrink-0">
            <SheetTitle className="text-xl font-semibold tracking-tight">
              {saveToListLabel}
            </SheetTitle>
            <SheetDescription className="sr-only">
              {saveToListLabel}
            </SheetDescription>
          </div>
          <div className="flex flex-col overflow-hidden flex-1 min-h-0">
            {pickerContent}
          </div>
        </MobileSheet>
      </>
    );
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-[300px] p-0" side="bottom" align="start">
        {pickerContent}
      </PopoverContent>
    </Popover>
  );
}
